import { Dialog, DialogActions, DialogContent, DialogTitle, Fab as FloatingActionButton, Stack } from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import type { FC } from "react";
import { useState } from "react";
import type { Cost } from "../costTypes";
import InvoicesBasket from "./InvoicesBasket";
import PaymentBundles from "./PaymentBundles";
import CancelButton from "../../components/cancelButton/CancelButton";
import { usePaymentBundles } from "./usePaymentBundles";
import { Decimal } from "decimal.js";

interface PaymentsDialogProps {
  readonly costs: Pick<Cost, "id" | "bookingNumber" | "invoices" | "provider" | "currency">[];
  readonly open: boolean;
  onClose: () => void;
}

const PaymentsDialog: FC<PaymentsDialogProps> = ({ costs, open, onClose }) => {
  const [showPaidBundles, setShowPaidBundles] = useState(false);
  const { paymentBundles, isLoading, onCreateBundle, onMarkBundleAsPaid, onDeleteBundle } =
    usePaymentBundles(showPaidBundles);
  const invoicesInBasket = costs.flatMap((c) =>
    c.invoices
      .filter((i) => i.status === "FOR_PAYMENT")
      .map((i) => ({
        costId: c.id,
        bookingNumber: c.bookingNumber,
        provider: c.provider,
        currency: c.currency,
        invoice: {
          invoiceNumber: i.invoiceNumber,
          amount: i.amount === undefined ? undefined : new Decimal(i.amount),
        },
      })),
  );
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Płatności</DialogTitle>
      <DialogContent>
        <Stack spacing={1}>
          <InvoicesBasket
            items={invoicesInBasket}
            onCreateBundle={async (r) => {
              await onCreateBundle(r);
            }}
          />
          <PaymentBundles
            values={paymentBundles}
            isLoading={isLoading}
            showPaid={showPaidBundles}
            onShowPaidChange={setShowPaidBundles}
            onDelete={onDeleteBundle}
            onMarkAsPaid={onMarkBundleAsPaid}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={onClose}>Zamknij</CancelButton>
      </DialogActions>
    </Dialog>
  );
};

interface Props {
  readonly values: Pick<Cost, "id" | "bookingNumber" | "invoices" | "provider" | "currency">[];
}

const CostInvoicePayments: FC<Props> = ({ values }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <>
      <FloatingActionButton color="primary" title="Płatności" onClick={() => setDialogOpen(true)}>
        <PaymentIcon />
      </FloatingActionButton>
      <PaymentsDialog
        key={`batch-cost-invoice-dialog-${dialogOpen}`}
        costs={values}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
};

export default CostInvoicePayments;
